<template>
  <div :class="containerClass">
    <b-skeleton-wrapper :loading="isEmailLoading">
      <template #loading>
        <b-skeleton width="80%" type="text" height="20%" class="mt-5 mb-3"></b-skeleton>
      </template>
      <page-header
        :title="emailTitle || 'Email Configuration'"
        :breadcrumb="breadcrumb"
        :container-class="null"
        class="mb-16pt"
        show-back
      />
    </b-skeleton-wrapper>

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <div class="d-flex justify-content-end">
        <b-form-group label-class="form-label">
          <b-form-checkbox v-model="enabled" name="enabled-switch" switch :disabled="isEmailLoading">
            Enabled?
          </b-form-checkbox>
        </b-form-group>
      </div>

      <b-form-group label="Subject" label-for="subject" label-class="form-label">
        <b-form-input
          id="subject"
          placeholder="Enter subject for email"
          v-model="subject"
          required
          :disabled="isEmailLoading"
        />
      </b-form-group>

      <b-form-group label-for="body" label-class="form-label">
        <label>Body - <a href="#" class="text-primary" @click.prevent="openBookmarkDetails">Bookmark Details</a></label>
        <editor
          :api-key="tinemceApiKey"
          :init="{
            height: 400,
            menubar: false,
            branding: false,
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount'
            ],
            toolbar:
              'undo redo | formatselect | bookmarks | bold italic underline | ' +
              'alignleft aligncenter alignright | bullist numlist | link removeformat | help',
            setup: editor => {
              editor.ui.registry.addMenuButton('bookmarks', {
                text: 'Bookmarks',
                fetch: callback => {
                  const items = (EMAIL_BOOKMARKS[emailAbbr] || []).map(object =>
                    Object.assign({}, object, {
                      onAction: () => editor.insertContent('&nbsp;' + object.value)
                    })
                  );
                  callback(items);
                }
              });
            }
          }"
          v-model="body"
          placeholder="Enter body for email"
        />
      </b-form-group>

      <b-btn variant="primary" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>Update</span>
      </b-btn>
    </b-form>

    <bookmark-detail-modal :show-modal="showBookmarkDetails" @close="hideBookmarkDetails" :abbr="emailAbbr" />
  </div>
</template>

<script>
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import { mapActions } from 'vuex';
import Editor from '@tinymce/tinymce-vue';
import BookmarkDetailModal from '@/components/Ui/BookmarkDetailModal.vue';
import { EMAIL_BOOKMARKS } from '@/common/constants';

export default {
  name: 'EditEmailConfig',
  extends: Page,
  components: { Editor, BookmarkDetailModal, PageHeader },

  data() {
    return {
      title: 'Email Configuration',
      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,
      enabled: true,
      subject: null,
      body: null,
      emailTitle: null,
      emailAbbr: '',
      isEmailLoading: false,
      isLoading: false,
      showBookmarkDetails: false,
      EMAIL_BOOKMARKS
    };
  },

  computed: {
    isFormLoading() {
      return this.isLoading || this.isEmailLoading;
    },
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Email Configuration', to: { name: 'email-config-list' } },
        { text: 'Edit', active: true }
      ];
    }
  },

  methods: {
    ...mapActions('emailConfig', ['getEmail', 'updateEmail']),

    openBookmarkDetails() {
      this.showBookmarkDetails = true;
    },
    hideBookmarkDetails() {
      this.showBookmarkDetails = false;
    },

    async fetchEmail() {
      this.isEmailLoading = true;
      try {
        const res = await this.getEmail(this.$route.params.id);
        const data = res.data || res;
        this.emailTitle = data.title;
        this.enabled = data.enabled;
        this.emailAbbr = data.email_abbr;
        this.subject = data.subject;
        this.body = data.body;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        this.$router.push({ name: 'email-config-list' });
      }
      this.isEmailLoading = false;
    },

    async onSubmit() {
      this.isLoading = true;
      try {
        const data = {
          enabled: this.enabled,
          subject: this.subject,
          body: this.body
        };
        await this.updateEmail({ id: this.$route.params.id, data });
        this.makeToast({ variant: 'success', msg: 'Email Updated!' });
        setTimeout(() => this.$router.push({ name: 'email-config-list' }), 250);
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    }
  },

  mounted() {
    if (this.$route.params.id) {
      this.fetchEmail();
    }
  }
};
</script>
